import { haptic, prefersReducedMotion } from './fx'

type Kind = 'fire' | 'smoke' | 'spark' | 'debris'

type Particle = {
  kind: Kind
  x: number
  y: number
  vx: number
  vy: number
  r: number
  grow: number
  age: number
  life: number
  rot: number
  vr: number
  sprite?: number
  smoked?: boolean
}

const FUSE_MS = 1900
const ENTER_MS = 650
const SVG_NS = 'http://www.w3.org/2000/svg'

const random = (min: number, max: number) => Math.random() * (max - min) + min
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)

let running = false

function blob(inner: string, mid: string, size = 128) {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')!
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  g.addColorStop(0, inner)
  g.addColorStop(0.4, mid)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  return canvas
}

function bombSvg() {
  const wrap = document.createElement('div')
  wrap.innerHTML = `
    <svg viewBox="0 0 140 150" width="100%" height="100%" style="overflow:visible">
      <defs>
        <radialGradient id="bomb-body" cx="38%" cy="34%" r="70%">
          <stop offset="0" stop-color="#5b5d6b"/>
          <stop offset=".55" stop-color="#23242c"/>
          <stop offset="1" stop-color="#0c0c10"/>
        </radialGradient>
        <radialGradient id="bomb-glow">
          <stop offset="0" stop-color="#fff6c2"/>
          <stop offset=".35" stop-color="#ffc533" stop-opacity=".9"/>
          <stop offset="1" stop-color="#ff7a00" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <g class="bomb-shell">
        <ellipse cx="62" cy="144" rx="38" ry="5" fill="rgba(0,0,0,.18)"/>
        <rect x="84" y="36" width="26" height="18" rx="4" fill="#3a3b45" transform="rotate(40 97 45)"/>
        <rect x="86" y="38" width="22" height="5" rx="2" fill="#5d5f6c" transform="rotate(40 97 45)"/>
        <circle cx="62" cy="90" r="50" fill="url(#bomb-body)"/>
        <circle class="bomb-heat" cx="62" cy="90" r="50" fill="#ff3b1f" opacity="0"/>
        <ellipse cx="44" cy="70" rx="14" ry="8" fill="#fff" opacity=".28" transform="rotate(-35 44 70)"/>
        <circle cx="36" cy="82" r="3" fill="#fff" opacity=".35"/>
      </g>
      <path class="bomb-fuse-shadow" d="M104 36C110 24 128 27 119 13S125 2 132 5" fill="none" stroke="#6d5332" stroke-width="5" stroke-linecap="round" pathLength="1" stroke-dasharray="1 1"/>
      <path class="bomb-fuse" d="M104 36C110 24 128 27 119 13S125 2 132 5" fill="none" stroke="#e2bd84" stroke-width="2.6" stroke-linecap="round" pathLength="1" stroke-dasharray="1 1"/>
      <g class="bomb-spark">
        <circle r="14" fill="url(#bomb-glow)"/>
        <path d="M0-9L2-2 9 0 2 2 0 9-2 2-9 0-2-2z" fill="#fffbe6"/>
      </g>
    </svg>`
  return wrap
}

function shake(magnitude: number, duration: number) {
  const frames = Array.from({ length: 12 }, (_, i) => {
    const k = magnitude * (1 - i / 12)
    return {
      transform:
        i === 11 ? 'none' : `translate(${random(-k, k)}px, ${random(-k, k)}px) rotate(${random(-k, k) / 20}deg)`,
    }
  })
  const target = document.getElementById('root')
  if (!target) return
  const prev = document.documentElement.style.overflowX
  document.documentElement.style.overflowX = 'clip'
  const anim = target.animate(frames, { duration, delay: 60, easing: 'linear' })
  const done = () => {
    document.documentElement.style.overflowX = prev
  }
  anim.onfinish = done
  anim.oncancel = done
}

export function bombExplosion(fromX: number, fromY: number, onAfter?: () => void) {
  if (running) return
  if (prefersReducedMotion()) {
    haptic([30, 20, 60])
    onAfter?.()
    return
  }
  running = true

  const w = window.innerWidth
  const h = window.innerHeight
  const cx = w / 2
  const cy = h / 2
  const maxR = Math.hypot(w, h) / 2
  const dpr = Math.min(window.devicePixelRatio || 1, 2)

  const canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  canvas.width = w * dpr
  canvas.height = h * dpr
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    zIndex: '100001',
    pointerEvents: 'none',
  })
  document.body.appendChild(canvas)
  const ctx = canvas.getContext('2d')!
  ctx.scale(dpr, dpr)

  const fireSprites = [
    blob('rgba(255,255,240,1)', 'rgba(255,240,180,.8)'),
    blob('rgba(255,230,140,1)', 'rgba(255,190,60,.75)'),
    blob('rgba(255,170,60,1)', 'rgba(255,110,20,.65)'),
    blob('rgba(220,70,20,.9)', 'rgba(150,30,10,.5)'),
  ]
  const smokeSprite = blob('rgba(96,90,86,.55)', 'rgba(110,104,100,.28)')
  const glowSprite = blob('rgba(255,236,190,.85)', 'rgba(255,140,40,.35)', 256)
  const scale = Math.min(1.4, maxR / 1100 + 0.55)

  const size = Math.max(120, Math.min(220, Math.min(w, h) * 0.28))
  const bomb = bombSvg()
  Object.assign(bomb.style, {
    position: 'fixed',
    left: `${cx - size / 2}px`,
    top: `${cy - size * 0.58}px`,
    width: `${size}px`,
    height: `${size * (150 / 140)}px`,
    zIndex: '100002',
    pointerEvents: 'none',
    filter: 'drop-shadow(0 18px 24px rgba(0,0,0,.35))',
  })
  document.body.appendChild(bomb)
  bomb.animate(
    [
      {
        transform: `translate(${fromX - cx}px, ${fromY - cy}px) scale(.2) rotate(-260deg)`,
        opacity: 0.6,
      },
      { transform: 'none', opacity: 1 },
    ],
    { duration: ENTER_MS, easing: 'cubic-bezier(.2,1.25,.4,1)', fill: 'both' },
  )

  const shell = bomb.querySelector<SVGGElement>('.bomb-shell')!
  const heat = bomb.querySelector<SVGCircleElement>('.bomb-heat')!
  const fuse = bomb.querySelector<SVGPathElement>('.bomb-fuse')!
  const fuseShadow = bomb.querySelector<SVGPathElement>('.bomb-fuse-shadow')!
  const spark = bomb.querySelector<SVGGElement>('.bomb-spark')!
  const fuseLength = fuse.getTotalLength()

  const particles: Particle[] = []
  const start = performance.now()
  let last = start
  let boomAt = 0
  let lastTick = 0

  function boom() {
    bomb.remove()
    if (onAfter) window.setTimeout(onAfter, 700)
    haptic([60, 30, 120, 40, 200])
    shake(22, 900)
    const S = maxR * 1.7

    for (let i = 0; i < 190; i++) {
      const a = random(0, Math.PI * 2)
      const v = Math.pow(Math.random(), 0.5) * S
      particles.push({
        kind: 'fire',
        x: cx + random(-16, 16),
        y: cy + random(-16, 16),
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v * 0.75,
        r: random(45, 100) * scale,
        grow: random(80, 200) * scale,
        age: 0,
        life: random(0.6, 1.5),
        rot: 0,
        vr: 0,
        sprite: random(-0.15, 0.35),
      })
    }
    for (let i = 0; i < 170; i++) {
      const a = random(0, Math.PI * 2)
      const v = random(0.35, 1.3) * S
      particles.push({
        kind: 'spark',
        x: cx,
        y: cy,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v - random(0, 300),
        r: random(1.2, 2.6),
        grow: 0,
        age: 0,
        life: random(0.6, 1.6),
        rot: 0,
        vr: 0,
      })
    }
    for (let i = 0; i < 36; i++) {
      const a = random(0, Math.PI * 2)
      const v = random(0.3, 0.9) * S
      particles.push({
        kind: 'debris',
        x: cx,
        y: cy,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v - random(100, 500),
        r: random(4, 11),
        grow: 0,
        age: 0,
        life: random(1.4, 2.4),
        rot: random(0, Math.PI * 2),
        vr: random(-14, 14),
      })
    }
  }

  function update(p: Particle, dt: number) {
    p.age += dt
    if (p.age < 0) return
    const drag =
      p.kind === 'fire' ? 0.9 : p.kind === 'smoke' ? 0.93 : p.kind === 'spark' ? 0.975 : 0.985
    const k = Math.pow(drag, dt * 60)
    p.vx *= k
    p.vy *= k
    if (p.kind === 'spark') p.vy += 700 * dt
    if (p.kind === 'debris') p.vy += 1100 * dt
    if (p.kind === 'smoke') p.vy -= 40 * dt
    if (p.kind === 'fire') {
      p.vy -= 90 * dt
      if (!p.smoked && p.age / p.life > 0.55 && Math.random() < 0.7) {
        p.smoked = true
        particles.push({
          kind: 'smoke',
          x: p.x,
          y: p.y,
          vx: p.vx * 0.35 + random(-20, 20),
          vy: p.vy * 0.35 - random(10, 40),
          r: p.r * random(0.9, 1.4),
          grow: random(30, 70) * scale,
          age: 0,
          life: random(1.8, 3.2),
          rot: random(0, Math.PI * 2),
          vr: random(-0.5, 0.5),
        })
      }
    }
    p.x += p.vx * dt
    p.y += p.vy * dt
    p.r += p.grow * dt
    p.rot += p.vr * dt
  }

  function draw(p: Particle) {
    if (p.age < 0) return
    const t = p.age / p.life
    if (t >= 1) return
    if (p.kind === 'smoke') {
      const a = Math.min(1, p.age / 0.5) * Math.pow(1 - t, 1.4) * 0.55
      ctx.globalAlpha = a
      ctx.save()
      ctx.translate(p.x, p.y)
      ctx.rotate(p.rot)
      ctx.drawImage(smokeSprite, -p.r, -p.r, p.r * 2, p.r * 2)
      ctx.restore()
    } else if (p.kind === 'fire') {
      const stage = Math.max(0, Math.min(3, Math.floor((t + (p.sprite ?? 0)) * 4)))
      ctx.globalAlpha = 0.6 * Math.pow(1 - t, 1.2)
      ctx.drawImage(fireSprites[stage], p.x - p.r, p.y - p.r, p.r * 2, p.r * 2)
    } else if (p.kind === 'spark') {
      ctx.globalAlpha = 1 - t
      ctx.strokeStyle = `hsl(${40 + t * 10}, 100%, ${88 - t * 30}%)`
      ctx.lineWidth = p.r
      ctx.beginPath()
      ctx.moveTo(p.x, p.y)
      ctx.lineTo(p.x - p.vx * 0.035, p.y - p.vy * 0.035)
      ctx.stroke()
    } else {
      ctx.globalAlpha = Math.min(1, (1 - t) * 2)
      ctx.save()
      ctx.translate(p.x, p.y)
      ctx.rotate(p.rot)
      ctx.fillStyle = t < 0.25 ? '#ff9a3c' : '#2a1d18'
      ctx.fillRect(-p.r / 2, -p.r / 3, p.r, (p.r * 2) / 3)
      ctx.restore()
    }
  }

  function frame(now: number) {
    const dt = Math.min(0.05, (now - last) / 1000)
    last = now
    const elapsed = now - start
    ctx.clearRect(0, 0, w, h)

    if (!boomAt) {
      const t = Math.max(0, Math.min(1, (elapsed - ENTER_MS * 0.6) / FUSE_MS))
      fuse.style.strokeDashoffset = `${t}`
      fuseShadow.style.strokeDashoffset = `${t}`
      const pt = fuse.getPointAtLength(fuseLength * (1 - t))
      const flicker = random(0.75, 1.35)
      spark.setAttribute('transform', `translate(${pt.x} ${pt.y}) scale(${flicker}) rotate(${random(0, 90)})`)

      const amp = elapsed > ENTER_MS ? 1 + t * 7 : 0
      const swell = t > 0.8 ? 1 + (t - 0.8) * 0.9 : 1
      shell.setAttribute(
        'transform',
        `translate(${62 + random(-amp, amp) * 0.3} 90) rotate(${Math.sin(now / 38) * amp}) scale(${swell}) translate(-62 -90)`,
      )
      heat.setAttribute('opacity', String(Math.max(0, (t - 0.6) / 0.4) * 0.55 * random(0.7, 1)))

      if (elapsed > ENTER_MS * 0.6 && now - lastTick > 140) {
        lastTick = now
        haptic(4)
      }

      const ctm = spark.getScreenCTM()
      if (ctm && elapsed > ENTER_MS * 0.5) {
        const sx = ctm.e
        const sy = ctm.f
        for (let i = 0; i < 4; i++) {
          const a = random(-Math.PI, 0)
          const v = random(60, 220)
          particles.push({
            kind: 'spark',
            x: sx,
            y: sy,
            vx: Math.cos(a) * v,
            vy: Math.sin(a) * v,
            r: random(0.8, 1.8),
            grow: 0,
            age: 0,
            life: random(0.25, 0.55),
            rot: 0,
            vr: 0,
          })
        }
      }

      if (t >= 1) {
        boomAt = now
        boom()
      }
    }

    for (const p of particles) update(p, dt)

    ctx.globalCompositeOperation = 'source-over'
    for (const p of particles) if (p.kind === 'smoke' || p.kind === 'debris') draw(p)

    if (boomAt) {
      const bt = (now - boomAt) / 1000
      ctx.globalCompositeOperation = 'lighter'
      if (bt < 0.6) {
        const k = easeOut(bt / 0.6)
        const r = maxR * (0.25 + k * 0.45)
        ctx.globalAlpha = 0.8 * (1 - bt / 0.6)
        ctx.drawImage(glowSprite, cx - r, cy - r, r * 2, r * 2)
      }
      for (const [delay, width, alpha] of [
        [0, 34, 0.55],
        [0.08, 14, 0.35],
      ]) {
        const st = (bt - delay) / 0.7
        if (st > 0 && st < 1) {
          ctx.globalAlpha = alpha * (1 - st)
          ctx.strokeStyle = '#fff1d6'
          ctx.lineWidth = width * (1 - st) + 1
          ctx.beginPath()
          ctx.arc(cx, cy, maxR * 1.3 * easeOut(st), 0, Math.PI * 2)
          ctx.stroke()
        }
      }
    }

    ctx.globalCompositeOperation = 'lighter'
    for (const p of particles) if (p.kind === 'fire' || p.kind === 'spark') draw(p)

    if (boomAt) {
      const bt = (now - boomAt) / 1000
      if (bt < 0.22) {
        ctx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 1 - bt / 0.22
        ctx.fillStyle = '#fffdf5'
        ctx.fillRect(0, 0, w, h)
      }
    }
    ctx.globalAlpha = 1
    ctx.globalCompositeOperation = 'source-over'

    for (let i = particles.length - 1; i >= 0; i--) {
      if (particles[i].age >= particles[i].life) particles.splice(i, 1)
    }

    if (!boomAt || particles.length > 0 || now - boomAt < 800) {
      requestAnimationFrame(frame)
    } else {
      canvas.remove()
      running = false
    }
  }

  requestAnimationFrame(frame)
}
